import React, { useState, useContext, useEffect, useReducer } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { getDayName } from "../../tools";
import useKeyPress from "../../Components/useKeyPress";
import eventReducer from "./eventReducer";
import SchedulerContext from "../schedulerContext";
import AutocompleteInput from "../../Components/AutocompleteInput";
import TimeMenu from "./Components/TimeMenu";
import DeleteDialog from "./DeleteDialog";
import useInteraction from "../../UserSurveys/useInteractions";
import { useTranslation } from "react-i18next";
import useClasses from "../../useClasses";
import Reminders from "./Components/Reminders";

const styles = (theme) => ({
  header: {
    display: "flex",
    justifyContent: "flex-end",
    background: "rgba(0, 0, 0, 0.1)",
  },
  content: {
    padding: theme.spacing(2),
  },
  icon: {
    display: "flex",
    alignSelf: "center",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  iconButton: {
    padding: theme.spacing(1),
  },
});

function Edit({ initEvent, handlePopover }) {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  // client state
  const { eventsDispatch } = useContext(SchedulerContext);
  const [event, eventDispatch] = useReducer(eventReducer, initEvent);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const deletePress = useKeyPress(46);

  // server state
  const { interactions } = useInteraction();

  useEffect(() => {
    if (deletePress) setOpenDeleteDialog(true);
  }, [deletePress]);

  // temp update
  useEffect(() => {
    if (event) eventsDispatch({ type: "UPDATE_EVENT", payload: event });
  }, [event]);

  const handleSave = () => handlePopover({ type: "save", payload: event });

  const handleSurveyChange = (info, newValue) => {
    if (info && newValue?.id) {
      eventDispatch({ type: "UPDATE_SURVEY", payload: newValue });
    }
  };

  const handleTimeChange = (key) => (info, value) => {
    //calendarApi.scrollToTime(value);
    if (info) {
      eventDispatch({
        type: "UPDATE_TIME",
        payload: { key: key, info: info, value: value },
      });
    }
  };

  const handleLogicChange = (index) => (info) =>
    eventDispatch({
      type: "UPDATE_LOGIC",
      payload: { value: info.target.value, index },
    });

  return (
    <div>
      <div>
        <div className={classes.header}>
          <Tooltip title={t("common.buttons.remove")}>
            <IconButton
              aria-label="delete"
              onClick={() => setOpenDeleteDialog(true)}
              className={classes.iconButton}
              size="small"
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.actions.close")}>
            <IconButton
              aria-label="close"
              className={classes.iconButton}
              size="small"
              onClick={() => handlePopover({ type: "cancel" })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.content}>
          <div style={{ display: "flex", paddingBottom: 16 }}>
            <div className={classes.icon}>
              <AssignmentOutlinedIcon fontSize="small" />
            </div>
            <div>
              <AutocompleteInput
                width={200}
                options={interactions}
                getOptionLabel={(option) => option.name}
                value={{ name: event.title, id: event.itemId }}
                onChange={handleSurveyChange}
                variant="scheduler"
                textAlign="left"
              />
            </div>
          </div>

          <div style={{ display: "flex", paddingBottom: 16 }}>
            <div className={classes.icon}>
              <ScheduleOutlinedIcon fontSize="small" />
            </div>
            <div>
              <TimeMenu
                event={event}
                handleTimeChange={handleTimeChange}
                handleLogicChange={handleLogicChange}
              />
            </div>
          </div>
          {/** Reminder block */}
   {/*        <Box paddingBottom={2}>
            <Reminders
              intervals={event.reminderIntervals}
              eventDispatch={eventDispatch}
            />
          </Box> */}

          {/** Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSave}
              style={{ marginLeft: 16 }}
            >
              {t("common.actions.ok")}
            </Button>
          </div>
        </div>
      </div>
      <DeleteDialog
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        handleClose={handlePopover}
        day={getDayName(event.start)}
        title={
          interactions.find(({ id, name }) => id === event.itemId)?.name ?? ""
        }
      />
    </div>
  );
}

export default Edit;
